"use client";

import { useTranslations } from "next-intl";
import React, { useState, useEffect, useRef } from "react";

// Counter component that animates from 0 to the target value
const AnimatedCounter = ({
  end,
  duration = 2000,
  prefix = "",
  suffix = "",
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // Set up intersection observer to detect when counter is visible
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number | null = null;
    let animationFrameId: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Calculate current count using easeOutQuad easing function
      const easedProgress = 1 - (1 - progress) * (1 - progress);
      countRef.current = Math.floor(easedProgress * end);
      setCount(countRef.current);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [end, duration, isVisible]);

  return (
    <span ref={ref}>
      {prefix}
      {count}
      {suffix}
    </span>
  );
};

export default function Statistic() {
  const t = useTranslations("Stat");

  const statistics = [
    {
      title: t("name1"),
      value: "20",
      prefix: "+",
    },
    {
      title: t("name2"),
      value: "100",
      prefix: "+",
    },
    {
      title: t("name3"),
      value: "1200",
      prefix: "",
    },
    {
      title: t("name4"),
      value: "24",
      prefix: "",
    },
  ];

  return (
    <div
      id="stat"
      className="container text-center xl:pt-[6.1875rem] lg:pt-[3.75rem] pt-[2.5625rem] px-4"
    >
      <div className="max-w-[420px] xl:max-w-full mx-auto">
        <h1 className="font-extrabold xl:text-[3.5rem] lg:text-[2.5rem] text-[2rem] pt-[2.5625rem] lg:pt-0">
          {t("title")}
        </h1>
      </div>
      <div className="grid xl:grid-cols-4 md:grid-cols-2 grid-cols-1 pt-8 lg:gap-8 gap-4 lg:px-40">
        {statistics.map((stat, index) => (
          <div
            key={index}
            className="bg-[#F9F9F9] rounded-2xl px-6 py-6 items-start text-start w-full h-[7.875rem]"
          >
            <p className="text-[#262D33] font-semibold text-lg pb-3">
              {stat.title}
            </p>
            <p className="text-primary font-extrabold text-[2.375rem]">
              <AnimatedCounter
                end={parseInt(stat.value)}
                prefix={stat.prefix}
                duration={2000}
              />
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
